import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmployeeStatusesEnum,
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';
import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  SearchInput,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';


import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useChangeEmployeeStatus } from 'src/hooks/employees/change-employee-status.hook';
import { useGetEmployeesForTable } from 'src/hooks/employees/get-public-employees.hook';
import { CreateAppraisalInterview } from 'src/pages/employee/components/create-appraisal-interview';
import { getFiltersCount } from 'src/common/helpers/common.helpers';
import PermissionCheck from 'src/components/permission-check';

import { CreateAbsence } from './components/create-absence';
import { EmployeeListTable } from './components/employee-list-table';
import { EmployeeTableFilter } from './components/employee-table-filter';
import { EMPLOYEE_MODALS, INITIAL_EMPLOYEE_DATA } from './employee-list.constants';
import { IEmployee, IFilterData } from './employee-list.interfaces';
import { HeaderContainer } from './employee-list.styles';
import { CreateEmployeeButton } from './components/create-employee-button';

const EmployeeList = ({ limitedView }: { limitedView?: boolean }) => {
  const { t } = useTranslation(['employee', 'common']);
  const [activeModal, setActiveModal] = useState<null | string>(null);
  const [currentEmployeeData, setCurrentEmployeeData] = useState<IEmployee>(INITIAL_EMPLOYEE_DATA);
  const [filters, setFilters] = useState<IFilterData>({});

  const currentCompanyId = useGetCurrentCompanyId();
  const { employees, paginationConfig, loading, onTableChange } = useGetEmployeesForTable({
    searchParam: filters.searchParam || '',
    filter: {
      companyId: currentCompanyId,
      companyFilter: filters.companyFilter,
    },
  });

  const changeEmployeeStatus = useChangeEmployeeStatus();

  const closeModal = () => {
    setActiveModal(null);
    setCurrentEmployeeData(INITIAL_EMPLOYEE_DATA);
  };

  const onDeleteSubmit = () => {
    changeEmployeeStatus({
      variables: {
        payload: {
          memberId: currentEmployeeData._id,
          newStatus: EmployeeStatusesEnum.INACTIVE,
        },
      },
    });
    closeModal();
  };

  return (
    <div>
      <EmployeeTableFilter
        isOpened={activeModal === EMPLOYEE_MODALS.tableFilter}
        onClose={closeModal}
        setFilters={setFilters}
        filters={filters}
      />

      <CreateAppraisalInterview
        isOpened={activeModal === EMPLOYEE_MODALS.createInterview}
        onClose={closeModal}
        memberId={currentEmployeeData._id}
      />
      <DeleteModal
        translate={t}
        isOpened={activeModal === EMPLOYEE_MODALS.changeStatus}
        onClose={closeModal}
        submitBtnText={t('common:buttons.change')}
        title={t('employee:changeStatusModal.title')}
        subTitle={t('employee:changeStatusModal.subtitle', {
          employeeName: `${currentEmployeeData.profile.firstName} ${currentEmployeeData.profile.lastName}`,
        })}
        onSubmit={onDeleteSubmit}
      />
      <CreateAbsence
        isOpened={activeModal === EMPLOYEE_MODALS.createAbsence}
        onClose={closeModal}
        employeeId={currentEmployeeData._id}
      />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('employee:labels:employee')}
        </Typography>
        <div>
          <SearchInput
            value={filters.searchParam}
            onChange={e => setFilters({ ...filters, searchParam: e.target.value })}
          />
          <ButtonsWrapper>
            <Button
              type="primary"
              ghost
              icon={ICONS.filterAlt}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => setActiveModal(EMPLOYEE_MODALS.tableFilter)}
            >
              {`${t('common:buttons.filterBy')} ${getFiltersCount(filters.companyFilter)}`}
            </Button>
            {!limitedView && (
              <PermissionCheck
                permission={PLATFORM_PERMISSIONS.memberManagement}
                action={PERMISSION_ACTIONS.create}
              >
                <CreateEmployeeButton t={t} />
              </PermissionCheck>
            )}
          </ButtonsWrapper>
        </div>
      </HeaderContainer>
      <EmployeeListTable
        loading={loading}
        onTableChange={onTableChange}
        limitedView={limitedView}
        dataSource={employees}
        paginationConfig={paginationConfig}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setCurrentEmployeeData(record);
        }}
      />
    </div>
  );
};

export default EmployeeList;
